import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clearSubmitSuccess, completeInviteSignup } from '../../store/slices/registrationSlice';
import { verifyEmployeeInvitationToken } from '../../services/employeeInviteApi';
import './Registration.scss';

const Registration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { submitting, submitError, submitSuccess } = useSelector(state => state.registration);

  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  const [inviteLoading, setInviteLoading] = useState(true);
  const [inviteError, setInviteError] = useState('');
  const [invitedEmail, setInvitedEmail] = useState('');
  const [formData, setFormData] = useState({ displayName: '', password: '', confirmPassword: '' });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (submitSuccess) {
      const timer = setTimeout(() => {
        dispatch(clearSubmitSuccess());
        navigate('/');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [submitSuccess, dispatch, navigate]);

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      if (!token) {
        if (isMounted) {
          setInviteLoading(false);
          setInviteError('Missing invitation token. Please use your invitation URL from email.');
        }
        return;
      }
      try {
        setInviteLoading(true);
        setInviteError('');
        const verifyRes = await verifyEmployeeInvitationToken(token);
        if (!isMounted) return;
        setInvitedEmail(String(verifyRes.email || '').toLowerCase());
        setInviteLoading(false);
      } catch (error) {
        if (!isMounted) return;
        setInviteLoading(false);
        setInviteError(error.message || 'Invitation is invalid or expired.');
      }
    };
    run();
    return () => {
      isMounted = false;
    };
  }, [token]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const errors = {};
    if (!formData.password) errors.password = 'Password is required.';
    if (!formData.confirmPassword) errors.confirmPassword = 'Please confirm password.';
    if (formData.password && formData.password.length < 6) {
      errors.password = 'Password should be at least 6 characters long.';
    }
    if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    dispatch(
      completeInviteSignup({
        token,
        email: invitedEmail,
        password: formData.password,
        displayName: formData.displayName,
      })
    );
  };

  if (inviteLoading) {
    return (
      <div className="registration-container">
        <div className="registration-header">
          <h1>Employee Signup</h1>
          <p>Validating your invitation...</p>
        </div>
      </div>
    );
  }

  if (inviteError) {
    return (
      <div className="registration-container">
        <div className="registration-header">
          <h1>Employee Signup</h1>
          <p className="submit-error">{inviteError}</p>
          <p>
            If you need a new link, please contact your admin. You can also return to{' '}
            <Link to="/login">login</Link>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-container">
      <div className="registration-header">
        <h1>Employee Signup</h1>
        <p>Finish account setup using your invited official email.</p>
      </div>

      <div className="registration-form-container">
        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-section">
            <h3>Account setup</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="invite-email">Official Email</label>
                <input id="invite-email" type="email" value={invitedEmail} disabled />
              </div>
              <div className="form-group">
                <label htmlFor="displayName">Display Name (Optional)</label>
                <input
                  id="displayName"
                  name="displayName"
                  type="text"
                  value={formData.displayName}
                  onChange={handleChange}
                  placeholder="How your name appears in PPC"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={formErrors.password ? 'error' : ''}
                  placeholder="At least 6 characters"
                />
                {formErrors.password && <div className="error-text">{formErrors.password}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={formErrors.confirmPassword ? 'error' : ''}
                  placeholder="Re-enter password"
                />
                {formErrors.confirmPassword && (
                  <div className="error-text">{formErrors.confirmPassword}</div>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="spinner"></span>
                  Creating Account...
                </>
              ) : 'Create My Account'}
            </button>

            {submitError && <div className="submit-error">{submitError}</div>}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Registration;
